import React, { useMemo } from "react";
import { z } from "zod";

import { faCircleInfo } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	ButtonLoading,
	ButtonStandard,
	FormDatePicker,
	FormSingleSelect,
	FormSubscribe,
	Icon,
	ModalBase,
	PYForm,
	SubmitButton,
	Text,
	VALIDATE_ON,
} from "@performyard/py-components";
import type { BulkUpdateReviewCycleReportsRequestInput } from "@performyard/service-contracts";

import {
	type BulkAction,
	type DueDateBounds,
	type SelectValue,
	buildBulkUpdates,
	buildDueDateConflictMessage,
	buildSubtitleParts,
	countDueDateConflicts,
} from "../bulk-actions";

type BulkUpdates = BulkUpdateReviewCycleReportsRequestInput["updates"];

type BulkActionModalProps = {
	action: BulkAction;
	selectedCount: number;
	/** The selected forms' current due dates, which bound the one being set */
	selectedRows: DueDateBounds[];
	companyTimezone: string;
	/** Default time of day (hour) applied when the admin picks a due date */
	defaultDueTime: number;
	isSaving: boolean;
	onClose: () => void;
	onApplyUpdates: (_updates: BulkUpdates) => void;
	onConfirmDelete: () => void;
};

/** Centered heading plus "This action will change <b>10 forms</b> ..." */
const ModalHeader = ({ action, selectedCount }: { action: BulkAction; selectedCount: number }) => {
	const { prefix, count, suffix } = buildSubtitleParts(action, selectedCount);

	return (
		<div className="px-6 pb-4 text-center">
			<Text size="sub-heading" weight="semi-bold">
				{action.label}
			</Text>
			<div className="mt-2">
				<Text size="paragraph-3" weight="regular" data-testid="bulk-action-subtitle">
					{prefix}
					<Text overrideElement="span" size="paragraph-3" weight="semi-bold">
						{count}
					</Text>
					{suffix}
				</Text>
			</div>
		</div>
	);
};

const ModalFooter = ({
	isSaving,
	onClose,
	children,
}: {
	isSaving: boolean;
	onClose: () => void;
	children: React.ReactNode;
}) => (
	<div className="flex justify-center gap-2 px-6 pt-4 pb-6">
		<div className="w-fit">
			<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose} disabled={isSaving}>
				Cancel
			</ButtonStandard>
		</div>
		<div className="w-fit min-w-[120px]">{children}</div>
	</div>
);

/**
 * The modal opened by every bulk action. Value-setting actions render a single control and a
 * Save Changes button that stays disabled until a value is chosen. Delete swaps that for a
 * destructive confirm button, which is itself the acknowledgement.
 */
export const BulkActionModal = ({
	action,
	selectedCount,
	selectedRows,
	companyTimezone,
	defaultDueTime,
	isSaving,
	onClose,
	onApplyUpdates,
	onConfirmDelete,
}: BulkActionModalProps) => {
	return (
		<ModalBase isOpen onClose={onClose} dangerouslySetClassName="w-[432px]">
			<ModalHeader action={action} selectedCount={selectedCount} />

			{action.control === "confirmation" ? (
				<ModalFooter isSaving={isSaving} onClose={onClose}>
					{/* ButtonLoading only swaps in the spinner, so isSaving has to disable explicitly too */}
					<ButtonLoading
						color="red"
						size="large"
						loading={isSaving}
						onClick={onConfirmDelete}
						disabled={isSaving}
						data-testid="bulk-delete-confirm"
					>
						Yes, Delete Forms
					</ButtonLoading>
				</ModalFooter>
			) : (
				<ValueActionForm
					action={action}
					selectedRows={selectedRows}
					companyTimezone={companyTimezone}
					defaultDueTime={defaultDueTime}
					isSaving={isSaving}
					onClose={onClose}
					onApplyUpdates={onApplyUpdates}
				/>
			)}
		</ModalBase>
	);
};

//#region Value-setting actions
type ValueActionFormData = { dueDate: Date | null; selectValue: SelectValue | null };

const valueActionSchema = z.object({
	dueDate: z.date().nullable(),
	selectValue: z.enum(["enabled", "disabled"]).nullable(),
});

const ValueActionForm = ({
	action,
	selectedRows,
	companyTimezone,
	defaultDueTime,
	isSaving,
	onClose,
	onApplyUpdates,
}: {
	action: BulkAction;
	selectedRows: DueDateBounds[];
	companyTimezone: string;
	defaultDueTime: number;
	isSaving: boolean;
	onClose: () => void;
	onApplyUpdates: (_updates: BulkUpdates) => void;
}) => {
	const isDateAction = action.control === "date";

	const defaultValues = useMemo<ValueActionFormData>(() => ({ dueDate: null, selectValue: null }), []);

	/**
	 * The due-date rule depends on the selected forms' own dates, so the schema is built per
	 * selection rather than declared statically. Reported against `dueDate` so the design system
	 * renders it as that field's error: red label, red input border, message under the control.
	 */
	const schema = useMemo(() => {
		if (!isDateAction) return valueActionSchema;

		return valueActionSchema.superRefine((value, ctx) => {
			if (!value.dueDate) return;

			const conflictingCount = countDueDateConflicts({
				action,
				dateValue: value.dueDate,
				rows: selectedRows,
				companyTimezone,
			});
			if (conflictingCount === 0) return;

			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["dueDate"],
				message: buildDueDateConflictMessage({
					action,
					conflictingCount,
					selectedCount: selectedRows.length,
				}),
			});
		});
	}, [action, companyTimezone, isDateAction, selectedRows]);

	const handleSubmit = ({ value }: { value: ValueActionFormData }): void => {
		/**
		 * The form's own isSubmitting clears as soon as this returns, but the request is still in
		 * flight, so it can't be relied on to block a second submit. The Save button is disabled on
		 * isSaving; this guards the paths that bypass the button, such as Enter in a field.
		 */
		if (isSaving) return;

		onApplyUpdates(
			buildBulkUpdates({
				action,
				dateValue: value.dueDate,
				selectValue: value.selectValue ?? undefined,
				companyTimezone,
			})
		);
	};

	return (
		<PYForm
			id={`bulk-${action.key}`}
			schema={schema}
			defaultValues={defaultValues}
			// Validated as the date is picked so the conflict shows before Save is attempted
			validateOn={[VALIDATE_ON.ON_CHANGE, VALIDATE_ON.ON_SUBMIT]}
			onSubmit={handleSubmit}
		>
			<div className="flex flex-col gap-2 px-6 pb-2">
				{isDateAction ? (
					<FormDatePicker
						id="dueDate"
						label={action.fieldLabel}
						showTimeDropdown
						showMonthAndYearDropdowns
						defaultTime={defaultDueTime}
					/>
				) : (
					<FormSingleSelect<SelectValue>
						id="selectValue"
						label={action.fieldLabel}
						placeholder="Make a selection"
						options={action.options ?? []}
						selectionVariant="select"
					/>
				)}

				{action.showsTimezoneNote && (
					<div className="flex items-center gap-1" data-testid="bulk-action-timezone-note">
						<Icon icon={faCircleInfo} size={12} color="neutral-7" />
						<Text size="caption" weight="regular" color="neutral-7">
							Review cycle timezone is {companyTimezone}
						</Text>
					</div>
				)}
			</div>

			<FormSubscribe
				id={`bulk-${action.key}-subscribe`}
				selector={(state) => {
					const value = isDateAction ? state.values?.dueDate : state.values?.selectValue;
					// canSubmit goes false while a due date conflicts with the selected forms
					return !!value && state.canSubmit;
				}}
			>
				{(canSave) => (
					<ModalFooter isSaving={isSaving} onClose={onClose}>
						{/* `loading` only swaps in a spinner, so isSaving has to disable explicitly */}
						<SubmitButton color="primary" size="large" loading={isSaving} disabled={!canSave || isSaving}>
							Save Changes
						</SubmitButton>
					</ModalFooter>
				)}
			</FormSubscribe>
		</PYForm>
	);
};

export default BulkActionModal;
